// Aba "Atividades" do detalhe do contrato — toolbar (view switcher + filtros) +
// roteador entre Lista/Kanban/Gantt/Calendário + modal de criar/editar.
// Wave 2 (Antd) — 2026-06-01.

import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Segmented, Input, Select, Button, Tag, Empty, Table, Popconfirm, Tooltip,
  Typography, Space, Flex, App as AntApp,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  UnorderedListOutlined, AppstoreOutlined, BarChartOutlined, CalendarOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import type { Contrato, Atividade } from './types';
import { STATUS_ATIVIDADE, PRIORIDADE_ATIVIDADE, TIPO_ATIVIDADE } from './types';
import { fmtData, getToken } from './utils';
import { BarraProgresso } from './shared-components';
import { KanbanAtividades } from './AtividadesKanban';
import { GanttAtividades } from './AtividadesGantt';
import { CalendarioAtividadesOp } from './AtividadesCalendario';
import { ModalAtividade } from './ModalAtividade';

type View = 'lista' | 'kanban' | 'gantt' | 'calendario';

export function Atividades({ contrato, atividades, onAtualizar }: {
  contrato: Contrato; atividades: Atividade[]; onAtualizar: () => void;
}) {
  return <AntApp><AtividadesInner contrato={contrato} atividades={atividades} onAtualizar={onAtualizar} /></AntApp>;
}

function AtividadesInner({ contrato, atividades, onAtualizar }: {
  contrato: Contrato; atividades: Atividade[]; onAtualizar: () => void;
}) {
  const { message } = AntApp.useApp();
  const [view, setView] = useState<View>(
    (sessionStorage.getItem('op_ativ_view') as View) || 'lista'
  );
  useEffect(() => { sessionStorage.setItem('op_ativ_view', view); }, [view]);
  const [filtroResp, setFiltroResp] = useState<string>('');
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState<Atividade | null>(null);
  const [criando, setCriando] = useState(false);

  const responsaveis = Array.from(new Set(
    atividades.flatMap(a => (a.atribuicoes || []).map(x => x.email))
  )).sort();

  const filtradas = atividades.filter(a => {
    if (filtroResp && !(a.atribuicoes || []).some(x => x.email === filtroResp)) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return a.titulo.toLowerCase().includes(q) || (a.descricao || '').toLowerCase().includes(q);
    }
    return true;
  });

  async function moverStatus(atividadeId: string, novoStatus: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_mover_status_atividade', id: atividadeId, status: novoStatus },
        { headers: { Authorization: token } });
      await onAtualizar();
    } catch (e: any) {
      message.error('Erro ao mover atividade: ' + (e.response?.data?.error || e.message));
    }
  }

  async function excluirAtiv(a: Atividade) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_excluir_atividade', id: a.id },
        { headers: { Authorization: token } });
      message.success('Atividade excluída.');
      await onAtualizar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  return (
    <div>
      <Flex gap={10} wrap="wrap" align="center" style={{ marginBottom: 12 }}>
        <Segmented value={view} onChange={(v) => setView(v as View)}
          options={[
            { label: 'Lista', value: 'lista', icon: <UnorderedListOutlined /> },
            { label: 'Kanban', value: 'kanban', icon: <AppstoreOutlined /> },
            { label: 'Gantt', value: 'gantt', icon: <BarChartOutlined /> },
            { label: 'Calendário', value: 'calendario', icon: <CalendarOutlined /> },
          ]} />

        <Input.Search allowClear placeholder="Buscar..." value={busca}
          onChange={e => setBusca(e.target.value)} prefix={<SearchOutlined />}
          style={{ flex: 1, minWidth: 180, maxWidth: 280 }} />

        <Select value={filtroResp || ''} onChange={(v) => setFiltroResp(v || '')}
          placeholder="Todos responsáveis" allowClear style={{ minWidth: 180 }}
          options={responsaveis.map(r => ({ value: r, label: r }))} />

        <div style={{ flex: 1 }} />

        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCriando(true)}>
          Nova atividade
        </Button>
      </Flex>

      {/* Resumo de status */}
      {atividades.length > 0 && (
        <Space size={6} wrap style={{ marginBottom: 12 }}>
          {Object.entries(STATUS_ATIVIDADE).map(([k, v]) => {
            const c = atividades.filter(a => a.status === k).length;
            if (c === 0) return null;
            return (
              <Tag key={k} style={{ background: v.bg, color: v.cor, fontWeight: 600, fontSize: 11 }}>
                {v.label}: {c}
              </Tag>
            );
          })}
        </Space>
      )}

      {atividades.length === 0 ? (
        <Empty description='Sem atividades ainda. Clique em "+ Nova atividade" para começar — a Pam vai aprender com o histórico quais são as atividades default por área técnica.' />
      ) : view === 'lista' ? (
        <ListaAtividades atividades={filtradas} todas={atividades}
          onEditar={setEditando} onMoverStatus={moverStatus} onExcluir={excluirAtiv} />
      ) : view === 'kanban' ? (
        <KanbanAtividades atividades={filtradas}
          onEditar={setEditando} onMoverStatus={moverStatus} />
      ) : view === 'gantt' ? (
        <GanttAtividades atividades={filtradas} onEditar={setEditando} />
      ) : (
        <CalendarioAtividadesOp atividades={filtradas} onEditar={setEditando} />
      )}

      {(criando || editando) && (
        <ModalAtividade
          contrato={contrato}
          atividade={editando}
          atividadesDisp={atividades.filter(a => !editando || a.id !== editando.id)}
          onFechar={() => { setCriando(false); setEditando(null); }}
          onCriado={async (nova) => { setCriando(false); setEditando(nova); await onAtualizar(); }}
          onSalvo={async () => { setCriando(false); setEditando(null); await onAtualizar(); }}
        />
      )}
    </div>
  );
}

function ListaAtividades({ atividades, todas, onEditar, onMoverStatus, onExcluir }: {
  atividades: Atividade[];
  todas: Atividade[];
  onEditar: (a: Atividade) => void;
  onMoverStatus: (id: string, status: string) => void;
  onExcluir: (a: Atividade) => void;
}) {
  if (atividades.length === 0) return <Empty description="Nenhuma atividade corresponde aos filtros." />;

  const columns: ColumnsType<Atividade> = [
    {
      title: '', key: 'tipo', width: 28,
      render: (_, a) => (a.tipo ? TIPO_ATIVIDADE[a.tipo]?.emoji : '📌'),
    },
    {
      title: 'Atividade', key: 'titulo',
      render: (_, a) => {
        const predNomes = (a.predecessoras || [])
          .map(pid => todas.find(x => x.id === pid))
          .filter(Boolean) as Atividade[];
        const predPendentes = predNomes.filter(p => p.status !== 'concluida' && p.status !== 'cancelada');
        return (
          <div>
            <Typography.Text strong style={{ cursor: 'pointer' }} onClick={() => onEditar(a)}>
              {a.titulo}
            </Typography.Text>
            {a.descricao && (
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 10 }} ellipsis>{a.descricao}</Typography.Text>
              </div>
            )}
            {predPendentes.length > 0 && (
              <Typography.Text style={{ fontSize: 10, color: '#B07500', display: 'block', marginTop: 2 }}>
                ⏸ Aguarda: {predPendentes.map(p => p.titulo).join(', ')}
              </Typography.Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status', key: 'status', width: 130, align: 'center',
      render: (_, a) => {
        const st = STATUS_ATIVIDADE[a.status] || STATUS_ATIVIDADE.pendente;
        return (
          <Select value={a.status} onChange={(v) => onMoverStatus(a.id, v)}
            size="small" style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            options={Object.entries(STATUS_ATIVIDADE).map(([k, v]) => ({ value: k, label: v.label }))}
            // Borda colorida pra refletir status no select fechado
            dropdownStyle={{ minWidth: 130 }}
            styles={{
              root: { background: st.bg },
            }} />
        );
      },
    },
    {
      title: 'Prio.', key: 'prio', width: 60, align: 'center',
      render: (_, a) => {
        const pr = PRIORIDADE_ATIVIDADE[a.prioridade] || PRIORIDADE_ATIVIDADE.media;
        return <Tooltip title={pr.label}><span>{pr.emoji}</span></Tooltip>;
      },
    },
    {
      title: 'Responsáveis', key: 'resp', width: 200,
      render: (_, a) => (
        <Typography.Text style={{ fontSize: 10, color: '#555' }} ellipsis>
          {(a.atribuicoes || []).map(x => x.email).join(', ') || '—'}
        </Typography.Text>
      ),
    },
    {
      title: 'Prazo', key: 'prazo', width: 110, align: 'center',
      render: (_, a) => {
        const atrasada = a.data_termino_previsto
          && new Date(a.data_termino_previsto) < new Date()
          && a.status !== 'concluida' && a.status !== 'cancelada';
        return (
          <Typography.Text style={{ fontSize: 11, color: atrasada ? '#B91626' : '#555', fontWeight: atrasada ? 600 : 400 }}>
            {fmtData(a.data_termino_previsto)}{atrasada ? ' ⚠️' : ''}
          </Typography.Text>
        );
      },
    },
    {
      title: 'Progresso', key: 'prog', width: 100, align: 'center',
      render: (_, a) => <BarraProgresso pct={a.progresso_pct || 0} />,
    },
    {
      title: '', key: 'acoes', width: 80, align: 'right',
      render: (_, a) => (
        <Space size={2}>
          <Button size="small" type="text" icon={<EditOutlined />}
            onClick={() => onEditar(a)} title="Editar" />
          <Popconfirm title={`Excluir "${a.titulo}"?`}
            okText="Excluir" okButtonProps={{ danger: true }}
            onConfirm={() => onExcluir(a)}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} title="Excluir" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table<Atividade> size="small" rowKey="id" columns={columns} dataSource={atividades}
      pagination={false} />
  );
}
